/**
 * Snap to objects — pure geometry, no React / Redux / DOM.
 *
 * Mirrors Figma: while a selection is moved, its left edge, centre and right edge are pulled
 * onto the left edge, centre or right edge of any other object that comes within
 * `SNAP_THRESHOLD_PX`, and the same for top / middle / bottom. Every line the selection then
 * shares with another object is reported as a guide, so the canvas can draw the red
 * alignment lines between them.
 */

export type SnapRect = {
    left: number
    top: number
    width: number
    height: number
}

/** "x" snaps horizontally and is drawn as a VERTICAL line at `x`; "y" the other way round. */
export type SnapAxis = "x" | "y"

export type SnapDelta = {
    x: number
    y: number
}

/** Which axes the last snap actually pulled onto another object. */
export type SnappedAxes = {
    x: boolean
    y: boolean
}

export type SnapGuide = {
    axis: SnapAxis
    /** where the line sits on `axis` */
    position: number
    /** the extent of the line along the other axis, spanning every object aligned on it */
    start: number
    end: number
    /** points along the line where a cross marks an aligned edge or centre */
    markers: number[]
}

export type SnapResult = {
    delta: SnapDelta
    snapped: SnappedAxes
    guides: SnapGuide[]
}

/**
 * Screen px, divided by the canvas scale by the caller — the pull has to feel the same at
 * every zoom level, like Figma's.
 */
export const SNAP_THRESHOLD_PX = 5

export const NOT_SNAPPED: SnappedAxes = { x: false, y: false }

/**
 * Two lines closer than this are the same line. The rects are measured off the DOM and
 * divided by the canvas scale, so lines that are equal in the store come back a few
 * ten-thousandths apart.
 */
const ALIGNED_EPSILON = 0.01

/** start edge, centre, end edge — in this order, see `isCentreLine` */
const linesOf = (rect: SnapRect, axis: SnapAxis): number[] =>
    axis === "x"
        ? [rect.left, rect.left + rect.width / 2, rect.left + rect.width]
        : [rect.top, rect.top + rect.height / 2, rect.top + rect.height]

const isCentreLine = (lineIndex: number): boolean => lineIndex === 1

/** where the rect starts and ends along a guide drawn for `axis` */
const crossExtentOf = (rect: SnapRect, axis: SnapAxis): [number, number] =>
    axis === "x" ? [rect.top, rect.top + rect.height] : [rect.left, rect.left + rect.width]

/**
 * Figma marks the ends of an aligned EDGE, but only the middle of an aligned centre — the
 * centre line has no ends of its own on the guide.
 */
const markersOf = (rect: SnapRect, axis: SnapAxis, lineIndex: number): number[] => {
    const [start, end] = crossExtentOf(rect, axis)
    return isCentreLine(lineIndex) ? [(start + end) / 2] : [start, end]
}

export const translateRect = (rect: SnapRect, delta: SnapDelta): SnapRect => ({
    ...rect,
    left: rect.left + delta.x,
    top: rect.top + delta.y,
})

/** The smallest bounding box over `rects`, or null when there are none. */
export const unionRects = (rects: SnapRect[]): SnapRect | null => {
    if (!rects.length) return null
    const left = Math.min(...rects.map((rect) => rect.left))
    const top = Math.min(...rects.map((rect) => rect.top))
    const right = Math.max(...rects.map((rect) => rect.left + rect.width))
    const bottom = Math.max(...rects.map((rect) => rect.top + rect.height))
    return { left, top, width: right - left, height: bottom - top }
}

/**
 * The shortest distance that would put one of `movingLines` onto a target's line, or null
 * when none is within `threshold`.
 */
const findSnapOffset = (
    movingLines: number[],
    targets: SnapRect[],
    axis: SnapAxis,
    threshold: number
): number | null => {
    let best: number | null = null
    targets.forEach((target) => {
        linesOf(target, axis).forEach((targetLine) => {
            movingLines.forEach((movingLine) => {
                const offset = targetLine - movingLine
                if (
                    Math.abs(offset) <= threshold &&
                    (best === null || Math.abs(offset) < Math.abs(best))
                ) {
                    best = offset
                }
            })
        })
    })
    return best
}

/**
 * One guide per line the (already snapped) rect shares with at least one target, spanning
 * the rect and every target aligned on that line.
 */
const collectGuides = (moved: SnapRect, targets: SnapRect[], axis: SnapAxis): SnapGuide[] => {
    const guides: SnapGuide[] = []

    linesOf(moved, axis).forEach((movedLine, movedIndex) => {
        // a zero-sized rect has its three lines on top of each other — draw them once
        if (guides.some((guide) => Math.abs(guide.position - movedLine) < ALIGNED_EPSILON)) {
            return
        }

        const aligned: { rect: SnapRect; lineIndex: number }[] = []
        targets.forEach((target) => {
            const lineIndex = linesOf(target, axis).findIndex(
                (targetLine) => Math.abs(targetLine - movedLine) < ALIGNED_EPSILON
            )
            if (lineIndex !== -1) aligned.push({ rect: target, lineIndex })
        })
        if (!aligned.length) return

        aligned.push({ rect: moved, lineIndex: movedIndex })
        const extents = aligned.map(({ rect }) => crossExtentOf(rect, axis))
        guides.push({
            axis,
            position: movedLine,
            start: Math.min(...extents.map(([start]) => start)),
            end: Math.max(...extents.map(([, end]) => end)),
            markers: aligned.flatMap(({ rect, lineIndex }) => markersOf(rect, axis, lineIndex)),
        })
    })

    return guides
}

/**
 * Snap a rect that has been moved by `delta` onto `targets`.
 *
 * @param start the moving rect where the drag started
 * @param delta how far the pointer has moved it, in the same units as the rects
 * @param threshold how close a line has to come to be pulled in, same units
 * @param lockedAxis the axis a shift-drag is restricted to, if any. The other axis must not
 *        move at all, so it is never pulled — but lines that happen to align on it still get
 *        their guide, as in Figma.
 */
export const snapRect = (
    start: SnapRect,
    delta: SnapDelta,
    targets: SnapRect[],
    threshold: number,
    lockedAxis: SnapAxis | undefined
): SnapResult => {
    const moving = translateRect(start, delta)
    const canSnap = (axis: SnapAxis) => lockedAxis === undefined || lockedAxis === axis

    const offsetX = canSnap("x")
        ? findSnapOffset(linesOf(moving, "x"), targets, "x", threshold)
        : null
    const offsetY = canSnap("y")
        ? findSnapOffset(linesOf(moving, "y"), targets, "y", threshold)
        : null

    const snappedDelta = {
        x: delta.x + (offsetX ?? 0),
        y: delta.y + (offsetY ?? 0),
    }
    const moved = translateRect(start, snappedDelta)

    return {
        delta: snappedDelta,
        snapped: { x: offsetX !== null, y: offsetY !== null },
        guides: guidesFor(moved, targets),
    }
}

/** Every line `rect` shares with a target, on both axes. */
export const guidesFor = (rect: SnapRect, targets: SnapRect[]): SnapGuide[] => [
    ...collectGuides(rect, targets, "x"),
    ...collectGuides(rect, targets, "y"),
]

/**
 * The edges a resize handle drags, where the gesture started — absolute canvas units. An
 * axis the handle does not drag (the left / right handles on y) is left out.
 */
export type DraggedEdges = {
    x?: number
    y?: number
}

/**
 * Snap a resize: pull each dragged EDGE onto the nearest edge or centre of a target.
 *
 * Only the edges move, so unlike a move the selection's own centre never snaps — the same
 * as in Figma. The returned delta puts the edge exactly on the line; turning it into a box
 * (opposite edge anchored, shift-proportional, mirrored) stays the handle's business.
 */
export const snapEdges = (
    edges: DraggedEdges,
    delta: SnapDelta,
    targets: SnapRect[],
    threshold: number
): { delta: SnapDelta; snapped: SnappedAxes } => {
    const offsetX =
        edges.x === undefined ? null : findSnapOffset([edges.x + delta.x], targets, "x", threshold)
    const offsetY =
        edges.y === undefined ? null : findSnapOffset([edges.y + delta.y], targets, "y", threshold)

    return {
        delta: { x: delta.x + (offsetX ?? 0), y: delta.y + (offsetY ?? 0) },
        snapped: { x: offsetX !== null, y: offsetY !== null },
    }
}
